import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

import Logo from "../images/logo1.png";
import layout from "../css/landing.module.css";
import styles from "../css/tithe.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faHouse,
  faArrowRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

function Paymoney() {
  const { church_id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [titheData, setTitheData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successModal, setSuccessModal] = useState(false);

  // ===== TODAY DATE =====
  const today = new Date();
  const itemdate = today.toLocaleDateString("en-GB");

  // ===== FETCH USER =====
  useEffect(() => {
    axios
      .get(`https://appserver.cemkharistemple.com/paytithe/${church_id}`)
      .then((res) => {
        setUser(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [church_id]);

  // ===== FETCH TITHE HISTORY =====
  useEffect(() => {
    axios
      .get(`https://appserver.cemkharistemple.com/testpage/${church_id}`)
      .then((res) => setTitheData(res.data))
      .catch((err) => console.error(err));
  }, [church_id]);

  // ===== GROUP BY YEAR + MONTH =====
  const groupedData = titheData.reduce((acc, row) => {
    const year = new Date(row.date).getFullYear();
    if (!acc[year]) acc[year] = { months: {} };

    const month = new Date(row.date).getMonth() + 1;
    if (!acc[year].months[month]) acc[year].months[month] = 0;

    acc[year].months[month] += parseInt(row.amount);
    return acc;
  }, {});

  const uniqueMonths = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12
  ];

  // ===== FORM STATE =====
  const [formData, setFormData] = useState({
    church_id,
    amount: "",
    date_entry: itemdate,
    date: "",
    month: "",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // ===== SAVE TITHE =====
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        "https://appserver.cemkharistemple.com/savetithe",
        { value: formData }
      );

      if (res.data.message === "Success") {
        setSuccessModal(true);
      } else {
        alert("Failed to save tithe");
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    }

    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  if (loading) return <div>Loading...</div>;
  if (!user) return <div>User not found</div>;

  const fullName = `${user.first_name} ${user.last_name}`;

  return (
    <div className={layout.container}>

      {/* ===== HEADER ===== */}
      <header className={layout.header}>
        <div className={layout.pageTitleWrap}>
          <h1 className={layout.pageTitle}>Financial Records</h1>
          <p className={layout.pageSubtitle}>Tithe Records</p>
        </div>

        <div className={layout.headerRight}>
          <div className={layout.userChip}>
            <FontAwesomeIcon icon={faUser} />
            <span>Admin</span>
          </div>

          <button
            className={layout.iconBtn}
            onClick={() => navigate("/paytithe")}
          >
            <FontAwesomeIcon icon={faHouse} />
          </button>

          <button
            className={`${layout.iconBtn} ${layout.dangerBtn}`}
            onClick={handleLogout}
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </button>
        </div>
      </header>

      {/* ===== SIDEBAR ===== */}
      <aside className={layout.sidebar}>
        <div className={layout.sideTop}>
          <img src={Logo} className={layout.logoImg} alt="" />
          <div>Kharis Admin</div>
        </div>
      </aside>

      {/* ===== MAIN ===== */}
      <main className={layout.main}>
        <div className={styles.outerwrapper}>
          <div className={styles.memberHeader}>
            <div className={styles.memberId}>{user.church_id}</div>
            <div className={styles.memberName}>{fullName}</div>
          </div>

          {/* ===== TITHE TABLE ===== */}
          <div className={styles.tablewrapper}>
            <table>
              <thead>
                <tr>
                  <th>Year</th>
                  {uniqueMonths.map((month) => (
                    <th key={month}>
                      {new Date(2000, month - 1, 1).toLocaleString("default", {
                        month: "short",
                      })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).map(([year, { months }]) => (
                  <tr key={year}>
                    <td>{year}</td>
                    {uniqueMonths.map((month) => (
                      <td key={month}>{months[month] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ===== PAYMENT FORM ===== */}
          <form onSubmit={handleSave} className={styles.allitems}>
            <h3>Pay Tithe</h3>

            <div className={styles.groupinfo}>
              <div className={styles.textpers}>
                <label>Amount</label>
                <input
                  name="amount"
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.textpers}>
                <label>Month</label>
                <select
                  name="month"
                  className={styles.dropdown}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Month</option>
                  {uniqueMonths.map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.groupinfo}>
              <div className={styles.textpers}>
                <label>Date</label>
                <input type="date" name="date" onChange={handleChange} required />
              </div>

              <div className={styles.textpers}>
                <label>Date Entry</label>
                <input value={itemdate} disabled />
              </div>
            </div>

            <div className={styles.formbuttons}>
              <button type="submit" className={styles.submit_button}>
                {loading ? "Saving..." : "Submit"}
              </button>
              <button
                type="button"
                className={styles.cancel_button}
                onClick={() => navigate("/paytithe")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* ===== SUCCESS MODAL ===== */}
      {successModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox}>
            <h3>Tithe Recorded 🎉</h3>
            <p>Payment saved and confirmation SMS sent.</p>
            <button
              className={styles.submit_button}
              onClick={() => navigate("/paytithe")}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Paymoney;